import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";

import { Collapsible } from "./collapsible";

interface AccordionContextValue {
  curItemId?: string | number | null | undefined;
  curGroupId?: string | number | null | undefined;
  selectGroupId: (id: string) => void;
}

const AccordionContext = createContext<AccordionContextValue | undefined>(undefined);
const AccordionGroupContext = createContext<string | undefined>(undefined);

interface ParentProps {
  children: ReactNode;
  initialItemId?: string | number | null | undefined;
  initialGroupId?: string | number | null | undefined;
}

export function Parent({ children, initialItemId, initialGroupId }: ParentProps): ReactNode {
  const [curGroupId, selectGroupId] = useState<string | number | null | undefined>(initialGroupId);

  const context = useMemo(
    () => ({
      curItemId: initialItemId,
      curGroupId,
      selectGroupId: (id: string) => selectGroupId(id),
    }),
    [initialItemId, curGroupId],
  );

  return (
    <AccordionContext.Provider value={context}>
      <div className="m-0">{children}</div>
    </AccordionContext.Provider>
  );
}

interface GroupProps {
  label: string;
  children: ReactNode;
  groupId?: string;
}

export function Group({ label, children, groupId }: GroupProps): ReactNode {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error("Group must be used within a Parent");
  }
  const { curGroupId, selectGroupId } = context;

  const actualGroupId = groupId || label;

  const handleClick = useCallback(() => selectGroupId(actualGroupId), [selectGroupId, actualGroupId]);

  const open = curGroupId === actualGroupId;
  return (
    <AccordionGroupContext.Provider value={actualGroupId}>
      <div className={`cursor-pointer text-sm my-2 border-b border-[color-mix(in_srgb,var(--sidebar-background-color),black_5%)] ${open ? "open" : "closed"}`}>
        <div
          className={`flex items-center justify-between px-4 py-3 transition-colors duration-200 font-semibold ${open ? "text-black border-b border-transparent" : "text-[#444]"}`}
          onClick={handleClick}
        >
          <span>{label} </span>
          <span className="text-[#999] text-base">
            {open ? <MdKeyboardArrowUp/> : <MdKeyboardArrowDown/>}
          </span>
        </div>
        <Collapsible open={open}>
          <div className="bg-white pb-3">{children}</div>
        </Collapsible>
      </div>
    </AccordionGroupContext.Provider>
  );
}

interface ItemProps {
  itemId: string | number;
  children: ReactNode;
}

export function Item({ itemId, children }: ItemProps): ReactNode {
  const context = useContext(AccordionContext);
  const parentGroupId = useContext(AccordionGroupContext);

  if (!context) {
    throw new Error("Item must be used within a Parent");
  }

  const { curItemId, curGroupId, selectGroupId } = context;
  const active = curItemId === itemId;

  if (active && curGroupId == null && parentGroupId != null) {
    selectGroupId(parentGroupId);
  }

  return (
    <div className={`group/item ${active ? "active" : ""}`}>
      <div className={`
        block overflow-hidden whitespace-nowrap text-ellipsis px-4 pl-8 py-2 text-sm border-l-4 transition-all duration-150
        ${active
        ? "bg-[#f0f7ff] border-l-[var(--accordion-item-active-text-color)] text-[var(--accordion-item-active-text-color)] font-semibold"
        : "border-l-transparent text-[var(--accordion-item-text-color)] hover:bg-[#fafafa] hover:text-[var(--accordion-item-hover-text-color)]"}
      `}>
        {children}
      </div>
    </div>
  );
}
